/**
 * Recherche — le champ qui cherche : la loupe, une croix qui efface, un
 * raccourci clavier qui y saute de n'importe où, Échap qui vide puis qui sort.
 *
 *   <Recherche valeur={q} onChange={setQ} placeholder="@compte, nom, groupe" raccourci="/" />
 *   const visibles = filtrerParRecherche(comptes, q, (c) => [c.username, c.nom, c.groupe]);
 *
 * C'est un `Champ` en pilule (la recette de « ce qu'on tape »), rien de plus :
 * étiquette, aide, tailles, `ref` passent tels quels. Le résultat (« 12 comptes »)
 * s'écrit à droite, en gris, tant qu'on cherche — l'écran le passe déjà traduit.
 *
 * `filtrerParRecherche` cherche comme un humain : sans les accents ni la casse,
 * chaque mot de la requête quelque part dans les textes de l'objet (« lil fr »
 * trouve « Lilou · France »). Une recherche côté serveur (une liste paginée)
 * n'appelle pas le filtre : elle envoie `valeur`.
 *
 * Successeur de la `Recherche` d'Helios (Analytics, Comptes) et des 48 loupes
 * écrites à la main au cockpit (conversations, Vault, membres, tickets, proxys…).
 */
import { useEffect, useId, useRef, type ReactNode, type Ref } from "react";

import { Badge } from "../Badge";
import { Button } from "../Button";
import { Champ, type TailleChamp } from "../Champ";
import { Search, X } from "../icons";
import { cn } from "../utils";
import "./saisie.css";

/** « / » saute dans la recherche quand on n'écrit pas ailleurs ; « mod+k », c'est Ctrl K (⌘K sur un Mac). */
export type RaccourciRecherche = "/" | "mod+k";

export interface RechercheProps {
  valeur: string;
  onChange: (valeur: string) => void;
  placeholder?: string;
  /** Au-dessus, en capitales. Sans étiquette visible, passer `ariaLabel`. */
  etiquette?: ReactNode;
  ariaLabel?: string;
  aide?: ReactNode;
  /** À droite, en gris, quand on cherche : « 12 comptes », « aucun ». */
  resultat?: ReactNode;
  /** Le raccourci qui y amène le focus, montré en touche tant que le champ est vide. */
  raccourci?: RaccourciRecherche;
  /** L'étiquette de la croix — déjà traduite (défaut « Effacer »). */
  effacerLabel?: string;
  /** Entrée : chercher maintenant (une recherche serveur sans attendre la frappe). */
  onEntree?: (valeur: string) => void;
  taille?: TailleChamp;
  autoFocus?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  ref?: Ref<HTMLInputElement>;
  /** La classe du CADRE (largeur) — `w-full`, `max-w-xs`. */
  className?: string;
}

function estMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
}

/** Le focus est-il déjà dans un endroit où l'on écrit ? Alors « / » est un caractère, pas un raccourci. */
function onEcritAilleurs(cible: EventTarget | null): boolean {
  if (!(cible instanceof HTMLElement)) return false;
  return cible.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(cible.tagName);
}

export function Recherche({
  valeur,
  onChange,
  placeholder,
  etiquette,
  ariaLabel,
  aide,
  resultat,
  raccourci,
  effacerLabel = "Effacer",
  onEntree,
  taille = "md",
  autoFocus,
  disabled,
  id,
  name,
  ref,
  className,
}: RechercheProps) {
  const interne = useRef<HTMLInputElement | null>(null);
  const idResultat = useId();

  useEffect(() => {
    if (!raccourci || disabled) return;
    const auClavier = (e: KeyboardEvent) => {
      const voulu =
        raccourci === "/"
          ? e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey && !onEcritAilleurs(e.target)
          : e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey) && !e.altKey;
      if (!voulu) return;
      e.preventDefault();
      interne.current?.focus();
      interne.current?.select();
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [raccourci, disabled]);

  const lier = (el: HTMLInputElement | null) => {
    interne.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = el;
  };

  const touche = raccourci === "/" ? "/" : raccourci === "mod+k" ? (estMac() ? "⌘K" : "Ctrl K") : null;
  const cherche = valeur !== "";

  const suffixe = cherche ? (
    <span className="flex items-center gap-2">
      {resultat !== undefined && (
        <span id={idResultat} className="whitespace-nowrap text-[0.85em] tabular-nums text-faint" aria-live="polite">
          {resultat}
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        className="si-dans-le-champ"
        aria-label={effacerLabel}
        title={effacerLabel}
        disabled={disabled}
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => {
          onChange("");
          interne.current?.focus();
        }}
      >
        <X weight="bold" aria-hidden />
      </Button>
    </span>
  ) : touche ? (
    <span className="hidden sm:inline-flex" aria-hidden>
      <Badge ton="gris">{touche}</Badge>
    </span>
  ) : undefined;

  return (
    <Champ
      ref={lier as Ref<HTMLInputElement | HTMLTextAreaElement>}
      id={id}
      name={name}
      type="search"
      role="searchbox"
      enterKeyHint="search"
      autoComplete="off"
      spellCheck={false}
      pilule
      taille={taille}
      etiquette={etiquette}
      aide={aide}
      aria-label={etiquette === undefined ? (ariaLabel ?? placeholder) : undefined}
      aria-keyshortcuts={raccourci === "/" ? "/" : raccourci === "mod+k" ? (estMac() ? "Meta+K" : "Control+K") : undefined}
      placeholder={placeholder}
      autoFocus={autoFocus}
      disabled={disabled}
      value={valeur}
      icone={<Search weight="bold" />}
      suffixe={suffixe}
      className={cn("si-recherche", className)}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          // Échap vide d'abord ; un second Échap rend la main (le panneau qui contient le champ l'attend).
          if (valeur !== "") {
            e.preventDefault();
            e.stopPropagation();
            onChange("");
          } else {
            (e.target as HTMLInputElement).blur();
          }
        } else if (e.key === "Enter" && onEntree) {
          e.preventDefault();
          onEntree(valeur);
        }
      }}
    />
  );
}

/* ── Chercher comme un humain ────────────────────────────────────────────── */

/** Sans accents, sans casse, espaces resserrés : « Élodie  F » → « elodie f ». */
export function normaliserRecherche(texte: string): string {
  return texte
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Chaque mot de la requête se trouve-t-il quelque part dans ces textes ? Une requête vide trouve tout. */
export function correspondALaRecherche(textes: string | readonly (string | null | undefined)[], requete: string): boolean {
  const mots = normaliserRecherche(requete).split(" ").filter(Boolean);
  if (mots.length === 0) return true;
  const botte = normaliserRecherche(typeof textes === "string" ? textes : textes.filter(Boolean).join(" "));
  return mots.every((m) => botte.includes(m));
}

/** Les éléments dont les textes contiennent chaque mot de la requête, dans leur ordre. */
export function filtrerParRecherche<T>(
  elements: readonly T[],
  requete: string,
  textes: (element: T) => string | readonly (string | null | undefined)[],
): T[] {
  if (normaliserRecherche(requete) === "") return [...elements];
  return elements.filter((el) => correspondALaRecherche(textes(el), requete));
}
